import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Cinza sem o `bg-muted` do shadcn, que aqui é a cor do texto apagado. */
export function Esqueleto({ className }: { className?: string }) {
  return <Skeleton className={cn('bg-surface-subtle', className)} />;
}

/** O cartão de cada bloco: título, uma linha de contexto e um atalho opcional para a tela completa. */
export function Cartao({
  titulo,
  subtitulo,
  atalho,
  children,
  className,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  atalho?: { rotulo: string; to?: string; onClick?: () => void };
  children: ReactNode;
  className?: string;
}) {
  const estiloAtalho = 'inline-flex shrink-0 items-center gap-xs text-[13px] font-bold text-accent hover:underline';

  return (
    <section className={cn('flex min-w-0 flex-col rounded-lg border border-border bg-surface p-md', className)}>
      <header className="mb-md flex items-start justify-between gap-md">
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold text-ink">{titulo}</h2>
          {subtitulo && <p className="text-[13px] text-muted">{subtitulo}</p>}
        </div>
        {atalho &&
          (atalho.to ? (
            <Link to={atalho.to} className={estiloAtalho}>
              {atalho.rotulo}
              <ArrowRight size={14} />
            </Link>
          ) : (
            <button type="button" onClick={atalho.onClick} className={estiloAtalho}>
              {atalho.rotulo}
              <ArrowRight size={14} />
            </button>
          ))}
      </header>
      {children}
    </section>
  );
}

/** Erro de um bloco não derruba os outros: cada um avisa no próprio lugar. */
export function ErroDoBloco({ oQue }: { oQue: string }) {
  return <p className="text-[13px] text-danger">Não foi possível carregar {oQue}. Recarregue a página para tentar de novo.</p>;
}

/**
 * Variação que já sabe se é boa notícia: para quem compra, cobre em queda é
 * verde; na folha a variação só informa, sem cor.
 */
export type Variacao = { texto: string; direcao: 'sobe' | 'desce' | 'estavel'; leitura: 'boa' | 'ruim' | 'neutra' };

export function CartaoIndicador({
  rotulo,
  valor,
  detalhe,
  variacao,
  icone: Icone,
  to,
  onClick,
}: {
  rotulo: string;
  valor: string;
  detalhe?: ReactNode;
  variacao?: Variacao | null;
  icone: LucideIcon;
  to?: string;
  /** Abre algo por cima da tela, como o modal de índices, em vez de navegar. */
  onClick?: () => void;
}) {
  const Seta = variacao?.direcao === 'sobe' ? ArrowUpRight : variacao?.direcao === 'desce' ? ArrowDownRight : null;
  const corVariacao =
    variacao?.leitura === 'boa' ? 'text-success' : variacao?.leitura === 'ruim' ? 'text-danger' : 'text-muted';

  const conteudo = (
    <>
      <span className="flex items-center gap-xs text-[13px] text-muted">
        <Icone size={15} className="shrink-0" />
        <span className="truncate">{rotulo}</span>
      </span>
      <span className="mt-xs block truncate text-[24px] leading-tight font-bold text-ink">{valor}</span>
      <span className="mt-xs flex flex-wrap items-center gap-x-sm gap-y-[2px] text-[12px]">
        {variacao && (
          <span className={cn('inline-flex items-center gap-[2px] font-bold tabular-nums', corVariacao)}>
            {Seta && <Seta size={14} />}
            {variacao.texto}
          </span>
        )}
        {detalhe && <span className="text-muted">{detalhe}</span>}
      </span>
    </>
  );

  const estilo = 'block min-w-0 rounded-lg border border-border bg-surface p-md';
  const clicavel = cn(estilo, 'text-left transition-colors hover:border-accent');

  if (to) {
    return (
      <Link to={to} className={clicavel}>
        {conteudo}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={clicavel}>
        {conteudo}
      </button>
    );
  }

  return <div className={estilo}>{conteudo}</div>;
}

export function EsqueletoIndicador() {
  return (
    <div className="rounded-lg border border-border bg-surface p-md">
      <Esqueleto className="h-[14px] w-[120px]" />
      <Esqueleto className="mt-sm h-[26px] w-[160px]" />
      <Esqueleto className="mt-sm h-[12px] w-[100px]" />
    </div>
  );
}
